<script lang="ts">
	import type { ResponsiveImageData } from '@tools/cms';

	let {
		responsiveImage,
		isLoaded = $bindable(false),
		lazy = true,
		includeCaption = false,
		minSize = 100
	}: {
		responsiveImage: ResponsiveImageData;
		isLoaded?: boolean;
		lazy?: boolean;
		includeCaption?: boolean;
		minSize?: number;
	} = $props();

	const maxSize = minSize * 40;
	const srcSetScalars = [0.1, 0.25, 0.5, 0.75, 1];

	let containerWidth = $state(0);
	let containerHeight = $state(0);
	let imgRef: HTMLImageElement | undefined = $state(undefined);
	let targetSrc: string | undefined = $state(undefined);

	let currentLoadedSize = { width: 0, height: 0 };

	/**
	 * NOTE: This effect is used to update the srcset when the responsiveImage changes
	 */
	let srcset: { url: string; width: number; height: number }[] = $derived.by(() => {
		currentLoadedSize = { width: 0, height: 0 };
		const newSrcset = [];

		const url = new URL(responsiveImage.src);
		const search = url.searchParams;

		search.set('auto', 'format');
		search.set('q', '70');

		for (let i = 0; i < srcSetScalars.length; i++) {
			const multiplier = srcSetScalars[i];
			const srcWidth = Math.floor(responsiveImage.width * multiplier);
			const srcHeight = Math.floor(responsiveImage.height * multiplier);
			if (srcWidth < minSize || srcWidth > maxSize || srcHeight < minSize || srcHeight > maxSize) {
				continue;
			}

			search.set('w', `${srcWidth}`);
			search.set('h', `${srcHeight}`);

			newSrcset.push({ url: url.toString(), height: srcHeight, width: srcWidth });
		}

		return newSrcset;
	});

	function findSrcSetForSize(width: number, height: number) {
		for (let i = 0; i < srcset.length; i++) {
			if (width <= srcset[i].width && height <= srcset[i].height) {
				return srcset[i];
			}
		}
	}

	/**
	 * NOTE: This effect is used to update the targetSrc when the container size changes
	 */
	$effect(() => {
		if (containerWidth === 0 || containerHeight === 0) return;

		const targetSrcset = findSrcSetForSize(containerWidth, containerHeight);
		if (!targetSrcset) {
			//NOTE: if no valid srcset is found, use the largest one
			// this could happen if the container is larger then the available sizes
			if (srcset.length > 0) targetSrc = srcset[srcset.length - 1].url;
			return;
		}

		if (targetSrcset.url === targetSrc) {
			//NOTE: if the current targetSrc is the same as the one we want, do nothing
			return;
		}

		if (
			currentLoadedSize.width >= targetSrcset.width &&
			currentLoadedSize.height >= targetSrcset.height
		) {
			//NOTE: if the current loaded image is larger then the one we want, do nothing
			return;
		}

		//NOTE: Finaly, update the targetSrc to the one we want
		targetSrc = targetSrcset.url;
		currentLoadedSize.width = targetSrcset.width;
		currentLoadedSize.height = targetSrcset.height;
	});

	/**
	 * NOTE: This effect is used to set up listeners for the image load event
	 */
	$effect(() => {
		if (!imgRef) return;
		isLoaded = imgRef.complete;

		const onLoaded = () => {
			imgRef?.removeEventListener('load', onLoaded);
			isLoaded = true;
		};

		if (!isLoaded) {
			imgRef.addEventListener('load', onLoaded);
		}

		return () => {
			imgRef?.removeEventListener('load', onLoaded);
		};
	});
</script>

<figure class="asset" bind:offsetWidth={containerWidth} bind:offsetHeight={containerHeight}>
	<noscript>
		<img
			class="ready"
			src={srcset[0]?.url ?? ''}
			alt={responsiveImage?.alt ?? ''}
			title={responsiveImage?.title ?? ''}
		/>
	</noscript>
	<img
		src={targetSrc ? targetSrc : lazy ? '' : (srcset[0]?.url ?? '')}
		alt={responsiveImage?.alt ?? ''}
		title={responsiveImage?.title ?? ''}
		class="jsonly"
		class:ready={(isLoaded && targetSrc) || !lazy}
		width={responsiveImage.width}
		height={responsiveImage.height}
		bind:this={imgRef}
		loading={!lazy ? 'eager' : undefined}
	/>
	{#if includeCaption && responsiveImage?.title}
		<figcaption class="text:xs">{responsiveImage.title}</figcaption>
	{/if}
</figure>

<style lang="scss">
	noscript {
		width: 100%;
		height: 100%;

		img {
			object-fit: cover;
		}
	}

	figure {
		margin: 0;
		position: relative;
		display: flex;
		flex-direction: column;
		height: 100%;
		width: 100%;
	}

	img {
		object-fit: cover;
		width: 100%;
		height: 100%;
		visibility: hidden;
		opacity: 0;
		transition: opacity 0.4s ease-in-out;
	}

	.ready {
		visibility: visible;
		opacity: 1;
	}
</style>
